import { useNavigate } from "react-router-dom";
import { Card } from "@/components/Shared";
import { formatINR, formatDate } from "@/lib/format";
import api from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { FileDown, ArrowLeft } from "lucide-react";

export const STATUS_COLOR = {
  Draft: "bg-[#E6E8F0] text-[#101D51]", Sent: "bg-[#3B82F6]/10 text-[#1D4ED8]", Accepted: "bg-[#0B7C5A]/10 text-[#0B7C5A]",
  Rejected: "bg-[#F83A84]/10 text-[#F83A84]", Expired: "bg-[#F59E0B]/15 text-[#B45309]", Cancelled: "bg-[#6B7189]/10 text-[#6B7189]",
  "Converted To Order": "bg-[#0B7C5A] text-white", "Converted To Invoice": "bg-[#0B7C5A] text-white",
  Confirmed: "bg-[#3B82F6]/10 text-[#1D4ED8]", "In Fulfilment": "bg-[#F59E0B]/15 text-[#B45309]", Delivered: "bg-[#0B7C5A]/10 text-[#0B7C5A]", Invoiced: "bg-[#0B7C5A] text-white",
  Issued: "bg-[#3B82F6]/10 text-[#1D4ED8]", "Partially Paid": "bg-[#F59E0B]/15 text-[#B45309]", Paid: "bg-[#0B7C5A] text-white", Overdue: "bg-[#F83A84]/10 text-[#F83A84]",
};

export function StatusPill({ status, testid }) {
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLOR[status] || "bg-[#E6E8F0]"}`} data-testid={testid}>{status}</span>;
}

export async function openPdf(path, filename) {
  try {
    const { data } = await api.get(path, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
    const w = window.open(url, "_blank");
    if (!w) { const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); }
  } catch { toast.error("Could not render PDF"); }
}

export function PdfButton({ path, filename, testid }) {
  return <Button variant="outline" className="h-10" onClick={() => openPdf(path, filename)} data-testid={testid}><FileDown className="w-4 h-4" /> PDF</Button>;
}

export function DocHeader({ back, number, status, kind, children }) {
  const navigate = useNavigate();
  return (
    <div className="mb-5">
      <button onClick={() => navigate(back)} className="text-sm text-[#6B7189] flex items-center gap-1 mb-3" data-testid="doc-back"><ArrowLeft className="w-4 h-4" /> {kind}s</button>
      <div className="flex flex-col lg:flex-row lg:items-start gap-3">
        <div className="flex items-center gap-2 flex-wrap flex-1">
          <h1 className="text-2xl font-bold text-[#101D51]">{number}</h1><StatusPill status={status} testid="doc-status" />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="doc-actions">{children}</div>
      </div>
    </div>
  );
}

export function CustomerBox({ c, extra }) {
  return (
    <Card className="p-4 text-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] mb-2">Bill to (snapshot)</div>
      <div className="font-semibold text-[#101D51]">{c.company || c.name}</div>
      {c.company && <div>{c.name}</div>}
      <div className="text-[#6B7189]">{c.billing_address}{c.city ? `, ${c.city}` : ""} {c.state}</div>
      {c.gstin && <div className="text-[#6B7189] font-mono text-xs mt-1">GSTIN {c.gstin}</div>}
      <div className="text-[#6B7189]">+91 {c.phone} · Place of supply: {c.place_of_supply}</div>
      {extra}
    </Card>
  );
}

export function LinesTable({ lines, showQtyTracking }) {
  return (
    <Card className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="doc-lines">
        <thead className="bg-[#F5F6FA] text-[#6B7189] text-xs"><tr>
          <th className="text-left p-2">Item</th><th className="text-right p-2">Qty</th>{showQtyTracking && <th className="text-right p-2">Invoiced</th>}
          <th className="text-right p-2">Rate</th><th className="text-right p-2">Disc</th><th className="text-right p-2">Taxable</th><th className="text-right p-2">GST</th><th className="text-right p-2">Total</th></tr></thead>
        <tbody>{lines.map((l) => (
          <tr key={l.id} className="border-t border-[#F0F1F5]">
            <td className="p-2"><div className="font-medium text-[#101D51]">{l.name}</div><div className="text-[11px] text-[#6B7189]">{l.code} · HSN {l.hsn}</div></td>
            <td className="p-2 text-right">{l.quantity} {l.unit}</td>{showQtyTracking && <td className="p-2 text-right">{l.invoiced_qty || 0}</td>}
            <td className="p-2 text-right">{formatINR(l.unit_price)}</td><td className="p-2 text-right">{l.discount_pct ? `${(l.discount_pct * 100).toFixed(1)}%` : "—"}</td>
            <td className="p-2 text-right">{formatINR(l.net)}</td><td className="p-2 text-right">{Math.round(l.gst_rate * 100)}%</td><td className="p-2 text-right font-medium">{formatINR(l.total)}</td>
          </tr>))}</tbody>
      </table>
    </Card>
  );
}

export function Totals({ d, children }) {
  const Row = ({ l, v, bold, testid }) => <div className={`flex justify-between py-1 ${bold ? "font-bold text-lg border-t border-[#101D51] mt-1 pt-2" : "text-sm"}`}><span>{l}</span><span data-testid={testid}>{v}</span></div>;
  return (
    <Card className="p-4 text-[#101D51]">
      <Row l="Subtotal" v={formatINR(d.subtotal)} />
      {d.discount_total > 0 && <Row l="Discount" v={`- ${formatINR(d.discount_total)}`} />}
      <Row l="Taxable value" v={formatINR(d.taxable_total)} />
      {d.is_intra_state ? <><Row l="CGST" v={formatINR(d.cgst_total)} /><Row l="SGST" v={formatINR(d.sgst_total)} /></> : <Row l="IGST" v={formatINR(d.igst_total)} />}
      <Row l="Grand total" v={formatINR(d.grand_total)} bold testid="doc-grand-total" />
      {children}
    </Card>
  );
}

export function Timeline({ history }) {
  return (
    <Card className="p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] mb-3">History</div>
      <ol className="relative border-l border-[#E6E8F0] ml-2 space-y-3" data-testid="doc-timeline">
        {[...(history || [])].reverse().map((h) => (
          <li key={`${h.at}-${h.status}`} className="ml-4"><span className="absolute -left-1.5 w-3 h-3 rounded-full bg-[#101D51] border-2 border-white" />
            <div className="text-sm font-medium text-[#101D51]">{h.status}{h.note ? <span className="text-[#6B7189] font-normal"> — {h.note}</span> : ""}</div>
            <div className="text-[11px] text-[#6B7189]">{formatDate(h.at)}</div></li>))}
      </ol>
    </Card>
  );
}

export function LinkedDocs({ items, title }) {
  const navigate = useNavigate();
  if (!items?.length) return null;
  return (
    <Card className="p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-[#6B7189] mb-2">{title}</div>
      {items.map((d) => (
        <button key={d.id} onClick={() => navigate(d.path)} className="w-full flex items-center justify-between py-1.5 text-sm hover:underline" data-testid={`linked-${d.number}`}>
          <span className="font-medium text-[#101D51]">{d.number} <span className="text-[#6B7189] font-normal">· {d.status}</span></span>
          {d.grand_total != null && <span>{formatINR(d.grand_total)}</span>}
        </button>))}
    </Card>
  );
}
